import React, {FC, useEffect, useState} from "react"
import moment from "moment-timezone"
import PropTypes from "prop-types"

import {H3} from "@irati/ui/src/Typography"
import {Card, Hr} from "@irati/ui/src/Layout"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import devToggles from "@irati/dev-toggles"
import Flex from "@irati/ui/src/Flex"
import {Col} from "@irati/ui/src/ColumnRow"
import {RouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import adminReporting, {UserSavings} from "@irati/adminReporting/services/adminReporting"
import {RedemptionReportDuration} from "@irati/types/generated/ops"
import Loading from "@irati/ui/src/Loading"

import Breakdown from "./Breakdown"
import Balance from "./Balance"
import Cta from "./Cta"
import SaveWith from "./SaveWith"

interface Props extends InjectIntl {
  ctaType?: "goToPerks" | "goToSavings"
}

const MySavings: FC<Props> = ({ctaType = "goToPerks"}, {apps}) => {
  const [savingsData, setSavingsData] = useState<UserSavings>()
  const [load, setLoad] = useState<Promise<void>>()

  useEffect(() => {
    const getSavings = async() => {
      const month = {
        month: moment().month() + 1,
        year: moment().year()
      }
      const data = await adminReporting.getUserSavings({duration: RedemptionReportDuration.Monthly, month})
      setSavingsData(data)
    }
    if (devToggles.showMySavings()) {
      setLoad(getSavings())
    }
  }, [])

  if (!devToggles.showMySavings() || !savingsData) {
    return null
  }

  const hasThisMonthSavings = savingsData.savings.total.value > 0
  const showTopCta = ctaType === "goToPerks"

  const ctaResponsiveRules = {
    xsHidden: hasThisMonthSavings,
    smHidden: true,
    mdHidden: true,
    lgHidden: true
  }
  const hideBottomItemOnDesktop = {
    smHidden: hasThisMonthSavings,
    mdHidden: hasThisMonthSavings,
    lgHidden: hasThisMonthSavings
  }
  const showTopBreakdownOnDesktop = {
    xsHidden: true,
    smHidden: false,
    mdHidden: false,
    lgHidden: false
  }
  const saveWithResponsiveRules = {
    xsHidden: true,
    smHidden: false,
    mdHidden: false,
    lgHidden: false
  }

  return (
    <Loading load={load} initLoad>
      <Flex justifyContent="space-between" alignItems="center" marginTop={2} gap={1}>
        <H3>
          <FormattedMessage id="platform.mySavings.card.header.copy" /> {moment().format("MMMM")}
        </H3>
        {showTopCta && (
          <RouterLink cta to="/" app={apps.MY_SAVINGS}>
            <Flex alignItems="center">
              <FormattedMessage id="platform.mySavings.topCTA.copy" />
              <Icon icon={IconTypes.arrowRight} marginLeft />
            </Flex>
          </RouterLink>
        )}
      </Flex>
      <Card marginTop={3} padding={2}>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={2}>
            <Balance savings={savingsData.savings} previousSavings={savingsData.previousSavings} />
            <Col padding={0} {...showTopBreakdownOnDesktop}>
              <Breakdown savings={savingsData.savings} />
            </Col>
          </Flex>
          <Col padding={0} xsHidden={!hasThisMonthSavings}>
            <Cta ctaType={ctaType} />
          </Col>
        </Flex>
        <Col padding={0} {...hideBottomItemOnDesktop}>
          <Hr margin={[2, 0]} />
        </Col>
        <Col padding={0} {...ctaResponsiveRules}>
          <Flex justifyContent="flex-end">
            <Cta ctaType={ctaType} />
          </Flex>
        </Col>
        <Col padding={0} {...saveWithResponsiveRules}>
          <SaveWith savings={savingsData.savings} />
        </Col>
        <Col padding={0} {...hideBottomItemOnDesktop}>
          <Breakdown savings={savingsData.savings} />
        </Col>
      </Card>
    </Loading>
  )
}

MySavings.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default injectIntl(MySavings)
